import { setApiBase, setAppVersion, setBeaconKey, useCloak } from '@smart-link/cloak-kit';
import { logoUrl } from '@smart-link/shared';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import { AppStoreTemplate } from './templates/AppStore';
import { CustomTemplate } from './templates/Custom';
import { DownloadTemplate } from './templates/Download';
import { PlayStoreTemplate } from './templates/PlayStore';
import { ShieldTemplate } from './templates/Shield';
import type { TemplateProps } from './templates/types';

// Build-time release version (repo-root VERSION file), injected by vite.config.
declare const __APP_VERSION__: string;
// Build timestamp (changes every build), injected by vite.config — debug overlay only.
declare const __BUILD_TIME__: string;

setApiBase(import.meta.env.VITE_API_BASE ?? '');
setBeaconKey(import.meta.env.VITE_BEACON_KEY ?? '');
setAppVersion(typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev');

const TEMPLATES: Record<string, ComponentType<TemplateProps>> = {
  shield: ShieldTemplate,
  playstore: PlayStoreTemplate,
  download: DownloadTemplate,
  appstore: AppStoreTemplate,
  custom: CustomTemplate,
};

function setIcon(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
  link.removeAttribute('type');
}

function debugBuildLabel() {
  const raw = typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : '';
  if (!raw) return 'BUILD ?';
  return `BUILD ${raw.replace('T', ' ').replace(/\.\d{3}Z$/, 'Z')}`;
}

/** ?dbg=1 调试浮层:把真实落地页的 state + verify 结果 + 链路日志显示在页面上,
 *  用于 in-app webview(看不到 console)里排查点击→跳转卡在哪。生产不带 ?dbg 不渲染。 */
function DebugOverlay({ status }: { status: string }) {
  const [, force] = useState(0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 300);
    return () => clearInterval(id);
  }, []);
  const w = window as unknown as {
    __SLDBG?: string[];
    __TC_VR?: { decision?: string; redirect?: string };
  };
  const vr = w.__TC_VR;
  const buildLabel = debugBuildLabel();
  return (
    <>
      {/* 右上角切换按钮:默认收起、不挡底部 CTA;点它展开/收起日志 */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          position: 'fixed',
          right: 8,
          top: 60,
          zIndex: 100000,
          minWidth: 56,
          height: 40,
          padding: '0 10px',
          borderRadius: 20,
          border: 0,
          background: open ? '#dc2626' : '#2563eb',
          color: '#fff',
          font: '11px/1 ui-monospace,monospace',
        }}
      >
        {open ? '收起✕' : buildLabel}
      </button>
      {open && (
        <div
          style={{
            // 面板放**顶部**,绝不覆盖底部 CTA 按钮(所有模板 CTA 都在底部) —— 否则展开时
            // 会盖住 "Download Now"、手指点到浮层而非按钮(排查这个问题时踩过的坑)。
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            maxHeight: '50vh',
            overflow: 'auto',
            background: 'rgba(0,0,0,.94)',
            color: '#9f9',
            font: '10px/1.35 ui-monospace,monospace',
            padding: '108px 8px 12px',
            zIndex: 99999,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          <div style={{ color: '#f472b6', fontWeight: 700 }}>
            BUILD={typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : '?'} ver=
            {typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '?'}
          </div>
          <div style={{ color: '#4ad', fontWeight: 700 }}>
            state={status} | verify=
            {vr ? `${vr.decision} redirect=${vr.redirect || '(EMPTY)'}` : 'null(未就绪)'}
          </div>
          <div style={{ color: '#fbbf24' }}>{navigator.userAgent}</div>
          {(w.__SLDBG ?? []).join('\n')}
        </div>
      )}
    </>
  );
}

export default function App() {
  const { state, onAccept, template, appName, logo, buttonColor, buttonText, buttonSubtitle } =
    useCloak();
  const dbg =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('dbg');
  const Template = TEMPLATES[template] ?? TEMPLATES.shield!;
  const appLogoUrl = logoUrl(logo);

  useEffect(() => {
    // 默认留空（不暴露 "Smart Link"）；用户配了 appName 才用配置值
    document.title = appName.trim();
    if (appLogoUrl) {
      setIcon('icon', appLogoUrl);
      setIcon('apple-touch-icon', appLogoUrl);
    }
  }, [appName, appLogoUrl]);

  return (
    <>
      <Template
        state={state}
        onAccept={onAccept}
        appName={appName}
        logoUrl={appLogoUrl}
        buttonColor={buttonColor}
        buttonText={buttonText}
        buttonSubtitle={buttonSubtitle}
      />
      {dbg && <DebugOverlay status={state.status} />}
    </>
  );
}
